import '@aws-amplify/ui-react/styles.css';
import { AWSCloudWatchProvider, Amplify, Analytics, Auth, Hub, Logger } from 'aws-amplify';
import './App.css';
import awsConfig from './aws-exports';
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import Chat from './views/Chat';
import EmbeddedChat from './views/EmbeddedChat';
import EmbeddedSurveyTools from './views/EmbeddedSurveyTools';
import TermsAndConditions from './views/TermsAndConditions';
import { Authenticator, CheckboxField } from '@aws-amplify/ui-react';
import { ThemeProvider, createTheme } from '@mui/material';

/* const isLocalhost = Boolean(
  window.location.hostname === "localhost" ||
    // [::1] is the IPv6 localhost address.
    window.location.hostname === "[::1]" ||
    // 127.0.0.1/8 is considered localhost for IPv4.
    window.location.hostname.match(
      /^127(?:\.(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)){3}$/
    )
);
*/
// Assuming you have two redirect URIs, and the first is for localhost and second is for production
/* const [
  developerRedirectSignIn,
  productionRedirectSignIn,
  localRedirectSignIn,
] = awsConfig.oauth.redirectSignIn.split(",");

const [
  developerRedirectSignOut,
  productionRedirectSignOut,
  localRedirectSignOut,
] = awsConfig.oauth.redirectSignOut.split(",");
*/
const updatedAwsConfig = {
  ...awsConfig,
  oauth: {},
  aws_cognito_social_providers: [],
  /* ...awsConfig.oauth,
    redirectSignIn: isLocalhost ? localRedirectSignIn : process.env.REACT_APP_ENV === 'main' ?  productionRedirectSignIn : developerRedirectSignIn,
    redirectSignOut: isLocalhost ? localRedirectSignOut : process.env.REACT_APP_ENV === 'main' ?  productionRedirectSignOut : developerRedirectSignOut, */
};

Amplify.configure(updatedAwsConfig);
Analytics.autoTrack('session', {
  enable: true,
  provider: 'AWSPinpoint',
});

// initialize the group reported in analytics to the value of the anon_group query parameter, or "none"
// (once a user authenticates, it will be updated to their cognito group)
const urlParams = new URLSearchParams(window.location.search);
const anonGroup = urlParams.get('anon_group');
try {
  await Analytics.updateEndpoint({
    UserAttributes: {
      group: [anonGroup || 'none'],
    },
  });
} catch (error) {
  console.error('Failed to update endpoint:', error);
}

const theme = createTheme({
  palette: {
    primary: {
      main: '#231942',
    },
    secondary: {
      main: '#71787E',
    },
    third: {
      main: '#D9D9D9',
    },
  },
});

const globalStyles = {
  backgroundColor: 'white',
  height: 'calc(100vh - calc(100vh - 100%))',
  fontFamily: "'Roboto', sans-serif",
  width: '100%',
  flex: 1,
};

const logger = new Logger('ALMITRA-PILOT-FE', 'DEBUG');

Amplify.register(logger);
logger.addPluggable(
  new AWSCloudWatchProvider({
    logGroupName: 'almitra-pilot-fe',
    logStreamName: process.env.REACT_APP_ENV,
    region: awsConfig.aws_project_region,
  }),
);

function App() {
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkUser(); // check if user is signed in when App component mounts
    Hub.listen('auth', data => {
      checkUser(data);
    }); // check when authentication state changes
  }, []);

  async function checkUser(data) {
    if (data) {
      const { payload } = data;
      if (payload.event === 'signOut') {
        return;
      }
    }
    try {
      const user = await Auth.currentAuthenticatedUser();
      setUser(user);

      // if we have a user and group, set the group for analytics
      if (user !== null && user.signInUserSession.accessToken.payload['cognito:groups']) {
        try {
          await Analytics.updateEndpoint({
            UserAttributes: {
              group: [user.signInUserSession.accessToken.payload['cognito:groups'][0] || 'none'],
            },
          });
        } catch (error) {
          console.error('Failed to update endpoint:', error);
        }
      }
    } catch {
      await Auth.signOut();
    }
  }

  async function signOut() {
    try {
      await Auth.signOut();
      setUser(null);

      window.location.reload();
    } catch (error) {
      console.log('error signing out:', error);
    }
  }

  const validateEmail = input => {
    const validRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
    if (input.match(validRegex)) {
      return true;
    } else {
      return false;
    }
  };

  const authComponents = {
    SignUp: {
      FormFields() {
        return (
          <>
            <Authenticator.SignUp.FormFields />
            <CheckboxField
              defaultChecked={true}
              name="custom:emailOptIn"
              value="yes"
              label="Stay in the loop by receiving product release emails and sneak peeks. "
            />
          </>
        );
      },
    },
  };

  const authServices = {
    async handleSignUp(formData) {
      let { username, password, attributes } = formData;
      username = username.toLowerCase();
      attributes.email = username;
      attributes['custom:emailOptIn'] = attributes['custom:emailOptIn'] || 'no';
      return Auth.signUp({
        username,
        password,
        attributes,
        autoSignIn: {
          enabled: true,
        },
      });
    },
    async validateCustomSignUp(formData) {
      let { username } = formData;
      username = username.toLowerCase();

      // username should be email
      if (!validateEmail(username)) {
        return {
          username: 'Please enter a valid email address',
        };
      }
    },
  };

  const authFormFields = {
    signIn: {
      username: {
        placeholder: 'Enter your email address or username here',
        isRequired: true,
        label: 'Email address (or username)',
      },
      password: {
        placeholder: 'Enter your password here',
      },
    },
    signUp: {
      username: {
        placeholder: 'Enter your email address here',
        isRequired: true,
        label: 'Email address',
      },
      password: {
        placeholder: 'Enter your password here',
      },
      confirm_password: {
        placeholder: 'Please enter your password again',
      },
    },
    resetPassword: {
      username: {
        placeholder: 'Enter your email address or username here',
        isRequired: true,
        label: 'Email address (or username)',
      },
    },
  };

  return (
    <div className="App" style={globalStyles}>
      <ThemeProvider theme={theme}>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={
              <Authenticator
                usernameAlias="email"
                signUpAttributes={[]}
                components={authComponents}
                services={authServices}
                formFields={authFormFields}
              >
                <Chat user={user} signOut={signOut} logger={logger} />
              </Authenticator>
            } />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/terms-and-conditions/" element={<TermsAndConditions />} />
            <Route path="/embedded" element={<EmbeddedChat logger={logger} />} />
            <Route path="/embedded-survey-tools" element={<EmbeddedSurveyTools logger={logger} />} />
          </Routes>
        </BrowserRouter>
      </ThemeProvider>
    </div>
  );
}

export default App;
